import EmptyState from 'components/EmptyState';
import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import { saveSection } from 'components/section-edit/saveSection';
import { SectionEdit, SectionEditForm, SectionEditorReady } from 'components/section-edit/SectionEdit';
import { useFrozenOnceLoaded } from 'components/section-edit/useFrozenOnceLoaded';
import { ReactNode, useState } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useFindMatterRatesQuery, useGetMatterByIdQuery, useUpdateMatterMutation } from 'store/api/matters';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { MatterDto, MatterForm } from 'types/matters';
import { MatterPricelist } from 'types/pricelists';
import { useClassifiers } from 'utils/useClassifiers';
import { requiresStillConfidentialConfirmationOnSave } from '../closing/confidentialReasonFormRules';
import { useConfirmCaseStillConfidential } from '../closing/useConfirmCaseStillConfidential';
import { MatterSectionId, matterDtoToForm, matterFormToRequest, matterSectionFields } from '../matterFormMapping';
import { AccountingFields } from './AccountingFields';
import { ArchivingFields } from './ArchivingFields';
import { CaseDetailsFields } from './CaseDetailsFields';
import { ClientPartiesFields } from './ClientPartiesFields';
import { InvoiceSpecFields } from './InvoiceSpecFields';
import { OpponentsFields } from './OpponentsFields';
import { OtherFields } from './OtherFields';
import { PricingFields } from './PricingFields';
import { TeamFields } from './TeamFields';
import { MATTER_FORM_CLASSIFIERS, MatterFormState, useMatterForm } from './useMatterForm';
import { useSettings } from 'utils/useSettings';
import { Setting } from 'types/back-office-settings';

const SECTION_FIELDS: Record<MatterSectionId, (form: MatterFormState) => ReactNode> = {
    details: (form) => <CaseDetailsFields form={form} />,
    team: (form) => <TeamFields form={form} />,
    'client-parties': (form) => <ClientPartiesFields form={form} />,
    opponents: (form) => <OpponentsFields form={form} />,
    pricing: (form) => <PricingFields form={form} />,
    'invoice-spec': (form) => <InvoiceSpecFields form={form} />,
    accounting: (form) => <AccountingFields form={form} />,
    archiving: (form) => <ArchivingFields form={form} />,
    other: (form) => <OtherFields form={form} />,
    'custom-fields': (form) => (
        <CustomFields
            control={form.methods.control as unknown as Control<FieldValues>}
            fields={form.customFields}
            allOptional={form.isAdministrativeMatter}
        />
    ),
};

interface SectionFormProps {
    section: MatterSectionId;
    matter: MatterDto;
    initialData: MatterForm;
    classifiers: Record<string, ClassifierData>;
    pricelists: MatterPricelist[];
    customFields: CustomField[];
    isAdministratorFieldEnabled: boolean;
    onClose: () => void;
}

function MatterSectionForm({
    section,
    matter,
    initialData,
    classifiers,
    pricelists,
    customFields,
    isAdministratorFieldEnabled,
    onClose,
}: SectionFormProps) {
    const form = useMatterForm({
        type: 'edit',
        initialData,
        classifiers,
        currencyAccessible: matter.currencyAccessible,
        hasConnectedInvoices: matter.hasConnectedInvoices,
        matter,
        entityCustomFields: customFields,
    });
    // The page's own query argument, so the refetch refreshes the case it shows.
    const { id: matterId = '' } = useParams();
    const { refetch } = useGetMatterByIdQuery(matterId);
    const [updateMatter] = useUpdateMatterMutation();
    const confirmCaseStillConfidential = useConfirmCaseStillConfidential();

    // The PUT replaces the whole case, so every other field goes as stored now: collections in full, and the price
    // lists as stored (null) unless this is the section that edits them.
    const onSave = (values: MatterForm) =>
        saveSection(values, {
            section,
            sectionFields: matterSectionFields(values, initialData),
            fetchLatest: () => refetch().unwrap(),
            toForm: (dto: MatterDto) =>
                matterDtoToForm(dto, {
                    classifiers,
                    pricelists,
                    customFieldValues: form.customFieldValuesOf(dto.customFields),
                    isAdministratorFieldEnabled,
                }),
            toRequest: async (edited, latest, latestDto) => {
                if (
                    requiresStillConfidentialConfirmationOnSave(edited, latestDto.matterStateTypeCode) &&
                    !(await confirmCaseStillConfidential())
                ) {
                    return null;
                }
                return matterFormToRequest(edited, {
                    customFields: form.mapCustomFieldsToDto(edited as unknown as Record<string, unknown>),
                    initialForm: latest,
                    withPricelists: section === 'pricing',
                    withPayers: form.isSharedPaymentEnabled,
                }).request;
            },
            update: (request) => updateMatter({ ...request, id: String(matter.id) }).unwrap(),
            onSaved: onClose,
        });

    if (form.isLoading) {
        return <Loader />;
    }
    return (
        <SectionEditForm methods={form.methods} onSave={onSave} onCancel={onClose} formKey="case-edit">
            {SECTION_FIELDS[section](form)}
        </SectionEditForm>
    );
}

interface EditorProps {
    section: MatterSectionId;
    matter: MatterDto;
    onClose: () => void;
}

function MatterSectionEditor({ section, matter, onClose }: EditorProps) {
    const isAdministratorFieldEnabled = useSettings().isSettingEnabled(Setting.ADMINISTRATOR_FIELD);
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: MATTER_FORM_CLASSIFIERS });
    const {
        customFields,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFields,
    } = useCustomFields({ entityType: EntityType.MATTER, fields: matter.customFields });

    // Only Pricing and Case details read the lists, and, as on the edit page, from a read made after the section
    // opened: Pricing saves every staged list, so a stale read would delete lists nobody touched, and Case details
    // checks the opening date against them. Every other section saves `pricelists: null`.
    const readsPricelists = section === 'pricing' || section === 'details';
    const {
        data: pricelists,
        fulfilledTimeStamp: pricelistsFulfilledAt,
        isError: isPricelistsError,
        refetch: refetchPricelists,
    } = useFindMatterRatesQuery(String(matter.id), { refetchOnMountOrArgChange: true, skip: !readsPricelists });
    const [openedAt] = useState(() => Date.now());
    const isPricelistsFresh =
        !readsPricelists || (pricelistsFulfilledAt !== undefined && pricelistsFulfilledAt >= openedAt);

    const isLoaded =
        !isLoadingClassifiers &&
        !isLoadingCustomFields &&
        isPricelistsFresh &&
        !isPricelistsError &&
        (!!pricelists || !readsPricelists);
    const initialData = useFrozenOnceLoaded(isLoaded, () =>
        matterDtoToForm(matter, {
            classifiers,
            pricelists: pricelists ?? [],
            customFieldValues: initialCustomFieldsObject,
            isAdministratorFieldEnabled,
        })
    );

    if (!initialData && isPricelistsError) {
        return (
            <>
                <SectionEditorReady />
                <EmptyState
                    variant="error"
                    errorTitleId="matters.pricelists-unavailable-title"
                    errorBodyId="matters.pricelists-unavailable-body"
                    onRetry={() => void refetchPricelists()}
                    surface="matter-section-pricelists"
                />
            </>
        );
    }
    if (!initialData) {
        return <Loader />;
    }
    return (
        <MatterSectionForm
            section={section}
            matter={matter}
            initialData={initialData}
            classifiers={classifiers}
            pricelists={pricelists ?? []}
            customFields={customFields}
            isAdministratorFieldEnabled={isAdministratorFieldEnabled}
            onClose={onClose}
        />
    );
}

interface MatterSectionProps {
    id: MatterSectionId;
    titleId: string;
    matter: MatterDto;
    /** The read view, handed the section's Edit control. */
    children: (edit: ReactNode) => ReactNode;
    /** Classes of the section while it edits. */
    className?: string;
    /** The read view's rows: those no field edits stay under the fields while the section edits. */
    rows?: Row[];
}

/** A case section on the record page that edits in place with the case form's fields for that section. */
export function MatterSection({ id, titleId, matter, children, className, rows }: MatterSectionProps) {
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            editor={(close) => <MatterSectionEditor section={id} matter={matter} onClose={close} />}
            className={className}
            rows={rows}
        >
            {children}
        </SectionEdit>
    );
}
